import React, { useEffect, useMemo, useState } from "react";
import { api, errorMessage } from "../lib/api";
import {
    Plus, Trash2, Save, Loader2, X, ShoppingCart,
    RotateCcw, Search, CircleDollarSign, Receipt, Ban, PackageX,
} from "lucide-react";
import { toast } from "sonner";
import PosNav from "./PosNav";

const TYPES = [
    { value: "order",   label: "SO — Pesanan Penjualan" },
    { value: "invoice", label: "INV — Penjualan Final" },
];
const PAYMENT_METHODS = [
    { value: "cash",     label: "Cash" },
    { value: "transfer", label: "Transfer Bank" },
    { value: "qris",     label: "QRIS" },
    { value: "piutang",  label: "Piutang" },
];
const STATUS_LABEL = {
    draft: "Draft", confirmed: "Confirmed", paid: "Paid",
    cancelled: "Cancelled", returned: "Returned",
};
const STATUS_COLOR = {
    draft: "bg-secondary text-secondary-foreground",
    confirmed: "bg-blue-100 text-blue-800",
    paid: "bg-green-100 text-green-800",
    cancelled: "bg-red-100 text-red-800",
    returned: "bg-orange-100 text-orange-800",
};

const rp = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");

export default function AdminPos() {
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("invoice"); // order | invoice | return | all
    const [q, setQ] = useState("");
    const [stats, setStats] = useState({ today: { total: 0, count: 0 }, last_30_days: { total: 0, count: 0 } });
    const [modal, setModal] = useState(null); // {mode:'create'|'edit'|'return', sale?}

    const refresh = async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (tab !== "all") params.type = tab;
            if (q) params.q = q;
            const [{ data }, { data: s }] = await Promise.all([
                api.get("/admin/pos/sales", { params }),
                api.get("/admin/pos/stats").catch(() => ({ data: null })),
            ]);
            setSales(Array.isArray(data) ? data : []);
            if (s) setStats(s);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, [tab, q]);  // eslint-disable-line react-hooks/exhaustive-deps

    const handleCancel = async (sale) => {
        if (!window.confirm(`Batalkan ${sale.code} (${sale.customer_name || "-"})?`)) return;
        try {
            await api.post(`/admin/pos/sales/${sale.id}/cancel`);
            toast.success("Berhasil dibatalkan");
            refresh();
        } catch (e) { toast.error(errorMessage(e)); }
    };

    return (
        <div className="page-container">
            <div className="mx-auto max-w-6xl px-4 py-8 sm:py-10">
                <PosNav
                    title="Penjualan (POS)"
                    subtitle="Kelola pesanan, penjualan, dan retur."
                    right={
                        <button
                            data-testid="pos-new-btn"
                            onClick={() => setModal({ mode: "create", sale: emptySale(tab === "order" ? "order" : "invoice") })}
                            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:opacity-90"
                        >
                            <Plus size={14} /> Transaksi Baru
                        </button>
                    }
                />

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    <StatCard icon={<CircleDollarSign size={18} />} label="Penjualan Hari Ini" total={stats.today.total} count={stats.today.count} testid="stat-today" />
                    <StatCard icon={<Receipt size={18} />} label="30 Hari Terakhir" total={stats.last_30_days.total} count={stats.last_30_days.count} testid="stat-month" />
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    {[
                        { k: "invoice", label: "Penjualan (INV)" },
                        { k: "order",   label: "Pesanan (SO)"   },
                        { k: "return",  label: "Retur (RET)"    },
                        { k: "all",     label: "Semua"          },
                    ].map((t) => (
                        <button
                            key={t.k}
                            data-testid={`pos-tab-${t.k}`}
                            onClick={() => setTab(t.k)}
                            className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${tab === t.k ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:bg-secondary"}`}
                        >{t.label}</button>
                    ))}
                    <div className="ml-auto relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            data-testid="pos-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Cari kode / customer / sales"
                            className="pl-9 pr-3 py-1.5 text-xs rounded-md border border-border bg-white w-64"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="p-10 text-center text-muted-foreground"><Loader2 className="mx-auto animate-spin" /></div>
                ) : sales.length === 0 ? (
                    <div className="p-10 text-center text-muted-foreground border border-dashed border-border rounded-lg">
                        <PackageX className="mx-auto mb-3" size={32} strokeWidth={1.25} />
                        Belum ada transaksi
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-border bg-white">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/60 text-left text-xs uppercase text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-2.5">Kode</th>
                                    <th className="px-3 py-2.5">Tgl</th>
                                    <th className="px-3 py-2.5">Customer</th>
                                    <th className="px-3 py-2.5">Sales</th>
                                    <th className="px-3 py-2.5">Bayar</th>
                                    <th className="px-3 py-2.5 text-right">Total</th>
                                    <th className="px-3 py-2.5">Status</th>
                                    <th className="px-3 py-2.5"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {sales.map((s) => (
                                    <tr key={s.id} data-testid={`pos-row-${s.id}`} className="hover:bg-secondary/30">
                                        <td className="px-3 py-2 font-mono text-xs">{s.code}</td>
                                        <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "2-digit" })}</td>
                                        <td className="px-3 py-2">{s.customer_name || "-"}<div className="text-xs text-muted-foreground">{s.customer_phone || ""}</div></td>
                                        <td className="px-3 py-2 text-xs">{s.sales_person || "-"}</td>
                                        <td className="px-3 py-2 text-xs">{PAYMENT_METHODS.find(x => x.value === s.payment_method)?.label || s.payment_method}</td>
                                        <td className="px-3 py-2 text-right font-medium">{rp(s.grand_total)}</td>
                                        <td className="px-3 py-2"><span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_COLOR[s.status] || "bg-secondary"}`}>{STATUS_LABEL[s.status] || s.status}</span></td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <button data-testid={`pos-edit-btn-${s.id}`} onClick={() => setModal({ mode: "edit", sale: s })} disabled={["cancelled","returned"].includes(s.status)} className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:hover:text-muted-foreground" title="Edit"><Save size={13} /></button>
                                            {s.type === "invoice" && s.status !== "returned" && s.status !== "cancelled" && (
                                                <button data-testid={`pos-return-btn-${s.id}`} onClick={() => setModal({ mode: "return", sale: s })} className="inline-flex h-7 w-7 items-center justify-center rounded text-orange-600 hover:bg-orange-50" title="Retur"><RotateCcw size={13} /></button>
                                            )}
                                            {!["cancelled","returned"].includes(s.status) && (
                                                <button data-testid={`pos-cancel-btn-${s.id}`} onClick={() => handleCancel(s)} className="inline-flex h-7 w-7 items-center justify-center rounded text-destructive hover:bg-destructive/10" title="Batal"><Ban size={13} /></button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {modal && <SaleModal modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); refresh(); }} />}
        </div>
    );
}

function StatCard({ icon, label, total, count, testid }) {
    return (
        <div data-testid={testid} className="rounded-lg border border-border bg-white p-4 flex items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">{icon}</div>
            <div className="flex-1">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-lg font-semibold">{rp(total)}</div>
                <div className="text-[11px] text-muted-foreground">{count} transaksi</div>
            </div>
        </div>
    );
}

function emptySale(type = "invoice") {
    return {
        type, status: type === "order" ? "draft" : "paid",
        customer_name: "", customer_phone: "",
        items: [], payment_method: "cash",
        discount_total: 0, tax_pct: 0, notes: "", sales_person: "",
    };
}

function SaleModal({ modal, onClose, onSaved }) {
    const isReturn = modal.mode === "return";
    const isEdit = modal.mode === "edit";
    const original = modal.sale;
    const [form, setForm] = useState(() => {
        if (isReturn) {
            return {
                ...emptySale("return"),
                customer_name: original.customer_name || "",
                customer_phone: original.customer_phone || "",
                sales_person: original.sales_person || "",
                items: (original.items || []).map(it => ({ ...it })),
                notes: `Retur dari ${original.code}`,
            };
        }
        return {
            type: original.type, status: original.status,
            customer_name: original.customer_name || "", customer_phone: original.customer_phone || "",
            items: (original.items || []).map(it => ({ ...it })),
            payment_method: original.payment_method || "cash",
            discount_total: original.discount_total || 0, tax_pct: original.tax_pct || 0,
            notes: original.notes || "", sales_person: original.sales_person || "",
        };
    });
    const [saving, setSaving] = useState(false);
    const [productSearch, setProductSearch] = useState("");
    const [productHits, setProductHits] = useState([]);

    useEffect(() => {
        if (!productSearch.trim()) { setProductHits([]); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/products", { params: { q: productSearch, limit: 8 } });
                setProductHits(data || []);
            } catch { setProductHits([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [productSearch]);

    const totals = useMemo(() => {
        let sub = 0;
        for (const it of form.items) {
            const line = (Number(it.qty) || 0) * (Number(it.unit_price) || 0);
            sub += line * (1 - (Number(it.discount_pct) || 0) / 100);
        }
        const redeemRp = (Number(form.loyalty_redeem_points) || 0) * 1000;
        const afterDisc = Math.max(0, sub - (Number(form.discount_total) || 0) - redeemRp);
        const tax = afterDisc * ((Number(form.tax_pct) || 0) / 100);
        return { subtotal: sub, grand: afterDisc + tax, redeemRp };
    }, [form]);

    const addProductRow = (p) => {
        setForm(f => ({
            ...f,
            items: [...f.items, {
                product_id: p.id, product_name: p.name, sku: p.sku || "",
                variant: "", qty: 1, unit_price: p.price || 0, discount_pct: 0, subtotal: p.price || 0,
            }],
        }));
        setProductSearch("");
        setProductHits([]);
    };
    const updateItem = (idx, k, v) => setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [k]: v } : it) }));
    const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

    const handleSave = async () => {
        if (!form.items.length) { toast.error("Minimal 1 item"); return; }
        setSaving(true);
        try {
            const payload = { ...form };
            payload.items = form.items.map(it => ({
                product_id: it.product_id || "", product_name: it.product_name || "",
                sku: it.sku || "", variant: it.variant || "",
                qty: Number(it.qty) || 0, unit_price: Number(it.unit_price) || 0,
                discount_pct: Number(it.discount_pct) || 0, subtotal: 0,
            }));
            payload.discount_total = Number(form.discount_total) || 0;
            payload.tax_pct = Number(form.tax_pct) || 0;
            payload.loyalty_redeem_points = Number(form.loyalty_redeem_points) || 0;
            if (isReturn) {
                await api.post(`/admin/pos/sales/${original.id}/return`, payload);
                toast.success("Retur berhasil dicatat");
            } else if (isEdit) {
                await api.put(`/admin/pos/sales/${original.id}`, payload);
                toast.success("Transaksi diperbarui");
            } else {
                await api.post("/admin/pos/sales", payload);
                toast.success("Transaksi tersimpan");
            }
            onSaved();
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
            <div data-testid="pos-modal" className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <h2 className="font-semibold">{isReturn ? `Retur ${original.code}` : isEdit ? `Edit ${original.code}` : "Transaksi Baru"}</h2>
                    <button onClick={onClose} data-testid="pos-modal-close" className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
                </div>
                <div className="p-4 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {!isReturn && (
                            <label className="block"><span className="label-eyebrow text-muted-foreground">Tipe</span>
                                <select data-testid="pos-form-type" value={form.type} onChange={(e) => setForm(f => ({ ...f, type: e.target.value, status: e.target.value === "order" ? "draft" : "paid" }))} disabled={isEdit} className="mt-1.5 w-full rounded-md border border-border bg-white px-3 py-2 text-sm disabled:opacity-60">
                                    {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                            </label>
                        )}
                        <label className="block"><span className="label-eyebrow text-muted-foreground">Status</span>
                            <select data-testid="pos-form-status" value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} className="mt-1.5 w-full rounded-md border border-border bg-white px-3 py-2 text-sm">
                                {["draft","confirmed","paid"].map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                            </select>
                        </label>
                        <label className="block"><span className="label-eyebrow text-muted-foreground">Nama Customer</span>
                            <input data-testid="pos-form-customer" value={form.customer_name} onChange={(e) => setForm(f => ({ ...f, customer_name: e.target.value }))} className="mt-1.5 w-full rounded-md border border-border bg-white px-3 py-2 text-sm" />
                        </label>
                        <label className="block"><span className="label-eyebrow text-muted-foreground">HP Customer</span>
                            <input data-testid="pos-form-phone" value={form.customer_phone} onChange={(e) => setForm(f => ({ ...f, customer_phone: e.target.value }))} className="mt-1.5 w-full rounded-md border border-border bg-white px-3 py-2 text-sm" />
                        </label>
                        <label className="block"><span className="label-eyebrow text-muted-foreground">Metode Bayar</span>
                            <select data-testid="pos-form-payment" value={form.payment_method} onChange={(e) => setForm(f => ({ ...f, payment_method: e.target.value }))} className="mt-1.5 w-full rounded-md border border-border bg-white px-3 py-2 text-sm">
                                {PAYMENT_METHODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                            </select>
                        </label>
                        <label className="block"><span className="label-eyebrow text-muted-foreground">Sales Person</span>
                            <input data-testid="pos-form-sales" value={form.sales_person} onChange={(e) => setForm(f => ({ ...f, sales_person: e.target.value }))} placeholder="Nama sales (utk komisi)" className="mt-1.5 w-full rounded-md border border-border bg-white px-3 py-2 text-sm" />
                        </label>
                    </div>

                    {/* Product picker */}
                    <div className="relative">
                        <span className="label-eyebrow text-muted-foreground">Cari Produk</span>
                        <div className="relative mt-1.5">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <input data-testid="pos-product-search" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Ketik nama/SKU produk..." className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-border bg-white" />
                        </div>
                        {productHits.length > 0 && (
                            <div className="absolute z-10 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-white shadow-lg">
                                {productHits.map(p => (
                                    <button key={p.id} data-testid={`pos-product-hit-${p.id}`} type="button" onClick={() => addProductRow(p)} className="block w-full text-left px-3 py-2 text-sm hover:bg-secondary">
                                        <div className="font-medium">{p.name}</div>
                                        <div className="text-xs text-muted-foreground">{p.sku || "-"} · {rp(p.price)}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Items table */}
                    <div className="overflow-x-auto rounded-md border border-border">
                        <table className="w-full text-xs">
                            <thead className="bg-secondary/60 text-left uppercase text-muted-foreground">
                                <tr>
                                    <th className="px-2 py-1.5">Produk</th>
                                    <th className="px-2 py-1.5 w-16">Qty</th>
                                    <th className="px-2 py-1.5 w-28">Harga</th>
                                    <th className="px-2 py-1.5 w-14">Disc%</th>
                                    <th className="px-2 py-1.5 w-28 text-right">Subtotal</th>
                                    <th className="px-2 py-1.5 w-8"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {form.items.length === 0 ? (
                                    <tr><td colSpan={6} className="px-2 py-4 text-center text-muted-foreground">Cari produk di atas untuk menambahkan item</td></tr>
                                ) : form.items.map((it, i) => {
                                    const line = (Number(it.qty) || 0) * (Number(it.unit_price) || 0);
                                    const sub = line * (1 - (Number(it.discount_pct) || 0) / 100);
                                    return (
                                        <tr key={i}>
                                            <td className="px-2 py-1"><div className="font-medium">{it.product_name}</div><div className="text-[10px] text-muted-foreground">{it.sku}</div></td>
                                            <td className="px-2 py-1"><input data-testid={`pos-item-qty-${i}`} type="number" min="0" value={it.qty} onChange={(e) => updateItem(i, "qty", e.target.value)} className="w-full rounded border border-border bg-white px-2 py-1 text-xs" /></td>
                                            <td className="px-2 py-1"><input data-testid={`pos-item-price-${i}`} type="number" min="0" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className="w-full rounded border border-border bg-white px-2 py-1 text-xs" /></td>
                                            <td className="px-2 py-1"><input data-testid={`pos-item-disc-${i}`} type="number" min="0" max="100" value={it.discount_pct} onChange={(e) => updateItem(i, "discount_pct", e.target.value)} className="w-full rounded border border-border bg-white px-2 py-1 text-xs" /></td>
                                            <td className="px-2 py-1 text-right">{rp(sub)}</td>
                                            <td className="px-2 py-1"><button data-testid={`pos-item-del-${i}`} onClick={() => removeItem(i)} className="text-destructive hover:bg-destructive/10 rounded p-1"><Trash2 size={12} /></button></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="block"><span className="label-eyebrow text-muted-foreground">Diskon Total (Rp)</span>
                            <input data-testid="pos-form-disctotal" type="number" min="0" value={form.discount_total} onChange={(e) => setForm(f => ({ ...f, discount_total: e.target.value }))} className="mt-1.5 w-full rounded-md border border-border bg-white px-3 py-2 text-sm" />
                        </label>
                        <label className="block"><span className="label-eyebrow text-muted-foreground">Pajak (%)</span>
                            <input data-testid="pos-form-tax" type="number" min="0" max="100" step="0.1" value={form.tax_pct} onChange={(e) => setForm(f => ({ ...f, tax_pct: e.target.value }))} className="mt-1.5 w-full rounded-md border border-border bg-white px-3 py-2 text-sm" />
                        </label>
                        {!isReturn && (
                            <label className="block sm:col-span-2"><span className="label-eyebrow text-muted-foreground">Tukar Point Loyalty (1 pt = Rp 1.000, opsional)</span>
                                <input data-testid="pos-form-redeem" type="number" min="0" value={form.loyalty_redeem_points || 0} onChange={(e) => setForm(f => ({ ...f, loyalty_redeem_points: e.target.value }))} placeholder="0" className="mt-1.5 w-full rounded-md border border-border bg-white px-3 py-2 text-sm" />
                                <p className="mt-1 text-[10px] text-muted-foreground">Kalau customer punya point, isi jumlah point yang ditukar. Sistem otomatis kurangi grand total.</p>
                            </label>
                        )}
                    </div>
                    <label className="block"><span className="label-eyebrow text-muted-foreground">Catatan</span>
                        <textarea data-testid="pos-form-notes" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="mt-1.5 w-full rounded-md border border-border bg-white px-3 py-2 text-sm" />
                    </label>

                    <div className="rounded-md border border-border bg-secondary/30 p-3 space-y-1 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Subtotal Item</span><span>{rp(totals.subtotal)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">− Diskon</span><span className="text-destructive">− {rp(form.discount_total || 0)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">+ Pajak ({form.tax_pct || 0}%)</span><span>{rp(totals.grand - Math.max(0, totals.subtotal - (form.discount_total || 0)))}</span></div>
                        <div className="flex justify-between font-semibold text-base pt-2 border-t border-border" data-testid="pos-form-grand"><span>Grand Total</span><span>{rp(totals.grand)}</span></div>
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
                    <button onClick={onClose} className="rounded-md border border-border bg-white px-4 py-2 text-sm hover:bg-secondary">Batal</button>
                    <button data-testid="pos-form-save" onClick={handleSave} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-50">
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <ShoppingCart size={14} />} Simpan
                    </button>
                </div>
            </div>
        </div>
    );
}
